import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";
import toast from "react-hot-toast";

import Constants from "../../../../common/CustomerConstants";
import Layout from "../../../../Layout/Layout";
import Loader from "../../../../Components/Loader";
import { InputField } from "../../../../Components/formElements/InputField";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
import FileViewerModalForHB from "../../../../Components/FileViewer/FileViewerModalForHB";
import ItemService from "../../../../services/customer_appCommonService";
import { clearErfServiceNumbers } from "../../../../redux/feature/customer_app/customerSlice";
import ServicePaymentSection from "./ServicePaymentSection";
import StockInConfirmModal from "./StockInConfirmModal";
import {
  SERVICE_ACTION_CALL_STATUS,
  SERVICE_STATUS,
  JOB_STATUS,
  SERVICE_ACTION,
} from "./ServiceConstants";
import { hasRole } from "./serviceHelpers";
import ServiceTallyStatusSection from "./ServiceTallyStatusSection";
import FileUploadComponentForHB from "../../../../Components/formElements/FileUploadComponentForHB";

const ServiceDetailPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    return () => dispatch(clearErfServiceNumbers());
  }, [dispatch]);

  // ── Redux ──────────────────────────────────────────────────────────────────
  const serviceNr = useSelector(
    (state) => state?.customer?.erfServiceForm?.documentNumber,
  );
  const erfNr = useSelector(
    (state) => state?.customer?.erfServiceForm?.erfDocumentNumber,
  );
  const userData = useSelector((state) => state?.user);
  const roles = userData?.data?.roles || [];

  const isStore = hasRole(roles, "STORE");
  const isSE = hasRole(roles, "SERVICE-EXECUTIVE");

  const currentUser = {
    userId: userData?.data?.userId,
    name: userData?.data?.firstName || userData?.data?.name || "",
  };

  // ── State ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [serviceRecord, setServiceRecord] = useState(null);
  const [newBarcodeMap, setNewBarcodeMap] = useState({});
  const [returnedBarcodeMap, setReturnedBarcodeMap] = useState({});
  const [stockInModal, setStockInModal] = useState(null);
  const [completionAttachmentId, setCompletionAttachmentId] = useState("");
  const [fileViewModal, setFileViewModal] = useState({ show: false, id: null });
  const [seUpdateData, setSeUpdateData] = useState({
    completionStatus: SERVICE_STATUS.IN_PROGRESS,
    completionNotes: "",
    completionDate: "",
  });

  // Accordion
  const [open, setOpen] = useState({
    patient: true,
    items: true,
    money: true,
    update: true,
  });

  const isExtensionService = serviceRecord?.activeDocumentType === "Extension";
  const activeDocNr =
    serviceRecord?.activeDocumentNr || serviceRecord?.equipmentRentNr;

  const toggle = (key) => setOpen((p) => ({ ...p, [key]: !p[key] }));

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchServiceRecord = useCallback(
    async (number) => {
      const docNr = number || serviceNr;
      if (!docNr) return;
      setLoading(true);
      try {
        const res = await ItemService.generateQuery({
          queryName: "ERF_Service_Detail",
          documentNumber: docNr,
          page: 1,
          per_page: 1,
          timeZone: "Asia/Calcutta",
        });
        const record = res?.data?.[0] || res?.data?.data?.[0];
        if (record) setServiceRecord(record);
        else toast.error("Service record not found");
      } catch {
        toast.error("Failed to load service record");
      } finally {
        setLoading(false);
      }
    },
    [serviceNr],
  );

  useEffect(() => {
    if (serviceNr) fetchServiceRecord(serviceNr);
    else if (erfNr) fetchLatestByErf(erfNr);
  }, [serviceNr, erfNr]); // eslint-disable-line

  const fetchLatestByErf = async (number) => {
    setLoading(true);
    try {
      const res = await ItemService.generateQuery({
        queryName: "ERF_Service_Detail",
        equipmentRentNr: number,
        sort: { createdAt: -1 },
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
      });
      const record = res?.data?.[0] || res?.data?.data?.[0];
      if (record) setServiceRecord(record);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  // Add this function near fetchMaterialRecord
  const validateReplacementBarcode = async (
    barcode,
    materialCode,
    itemType,
  ) => {
    const godownCode =
      itemType === "Sale" || itemType === "Service" ? "SL" : "RE";
    try {
      const res = await ItemService.generateQuery({
        queryName: "MaterilBarcodeList",
        page: 1,
        per_page: 1,
        rowFilter: {
          $and: [
            {
              godownCode,
              materialStatus: "StockIn",
              barcode,
              materialCode,
            },
          ],
        },
        timeZone: "Asia/Calcutta",
      });
      return (
        res?.status === 200 && Array.isArray(res.data) && res.data.length > 0
      );
    } catch {
      return false;
    }
  };

  const updateErfItemBarcode = async (item, newBarcode) => {
    try {
      // Fetch the current ERF record fresh
      const erfRes = await ItemService.generateQuery({
        queryName: isExtensionService ? "ERFExtDetail" : "ERFDetail",
        documentNumber: activeDocNr,
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
      });

      const erfRecord = erfRes?.data?.[0] || erfRes?.data?.data?.[0];
      if (!erfRecord) {
        toast.error("ERF record not found — barcode not updated in ERF");
        return;
      }

      // Determine which array: use itemType stored on the service item
      const arrayKey = item.itemType === "Sale" ? "saleItems" : "rentItems";

      const erfItems = erfRecord[arrayKey] || [];

      // Match by equipmentItemNr AND materialCode — safe even when same
      // materialCode appears in both rent and sale arrays
      let matchedCount = 0;
      const updatedErfItems = erfItems.map((erfItem) => {
        const isMatch =
          String(erfItem.equipmentItemNr) === String(item.equipmentItemNr) &&
          erfItem.materialCode === item.materialCode;

        if (isMatch && matchedCount === 0) {
          matchedCount++;
          // ONLY update barcode — nothing else
          return { ...erfItem, barcode: newBarcode };
        }
        return erfItem;
      });

      if (matchedCount === 0) {
        toast.error(
          `ERF item not found (equipmentItemNr: ${item.equipmentItemNr}, ` +
            `materialCode: ${item.materialCode}) — barcode not synced to ERF`,
        );
        return;
      }
      const barcodePayload = {
        documentNumber: activeDocNr,
        equipmentRentNr: activeDocNr,
        [arrayKey]: updatedErfItems,
      };
      if (isExtensionService) {
        await ItemService.updateExtendEquipmentRent(barcodePayload);
      } else {
        await ItemService.updateEquipmentRentForm(barcodePayload);
      }
    } catch (err) {
      console.error("updateErfItemBarcode error:", err);
      toast.error("Replacement assigned but ERF barcode sync failed");
    }
  };

  const fetchMaterialRecord = async (barcode) => {
    const res = await ItemService.generateQuery({
      queryName: "MaterilBarcodeList",
      rowFilter: { $and: [{ barcode }] },
      timeZone: "Asia/Calcutta",
    });
    return res?.status === 200 && res.data?.length > 0 ? res.data[0] : null;
  };

  // ── STORE: assign replacement barcode ─────────────────────────────────────
  const handleAssignBarcode = async (item) => {
    const newBarcode = (newBarcodeMap[item.lineItemId] || "").trim();
    if (!newBarcode) return toast.error("Enter replacement barcode");

    setLoading(true);
    try {
      const now = new Date().toISOString();

      // ── Step 1: Validate barcode against stock ──────────────────────────
      const isValid = await validateReplacementBarcode(
        newBarcode,
        item.materialCode,
        item.itemType, // "Rent" → godown "RE", "Sale" → godown "SL"
      );
      if (!isValid) {
        toast.error(
          `Barcode "${newBarcode}" is not in stock or does not match ` +
            `material code "${item.materialCode}"`,
        );
        return;
      }

      // ── Step 2: Fetch new barcode's material record ─────────────────────
      const matRecord = await fetchMaterialRecord(newBarcode);
      if (!matRecord) {
        toast.error("Material master record not found for this barcode");
        return;
      }

      // ── Step 3: Push history entry onto new barcode ─────────────────────
      const historyEntry = {
        status: "Stock Out",
        equipmentRentNr: serviceRecord.equipmentRentNr,
        equipmentItemNr: item.equipmentItemNr,
        erfTypeCode: serviceRecord.erfTypeCode || "RE",
        serviceAction: SERVICE_ACTION.REPLACEMENT,
        serviceNr: serviceRecord.erfServiceNumber,
        condition: "Good",
        updatedAt: now,
        updatedBy: currentUser.userId,
        updatedByName: currentUser.name,
      };

      await ItemService.updateMaterialBarcodeItem({
        barcode: newBarcode,
        documentNumber: matRecord.documentNumber,
        materialStatus: "StockOut",
        barCode: "Assigned",
        materialCode: item.materialCode,
        barcodeERFHistory: [
          ...(matRecord.barcodeERFHistory || []),
          historyEntry,
        ],
        // NOTE: do NOT set serviceAction here — it is not a barcode field
      });

      // ── Step 4: Update serviceRequiredItems — only barcode fields ────────
      const updatedItems = serviceRecord.serviceRequiredItems.map((si) =>
        si.lineItemId === item.lineItemId
          ? {
              ...si,
              newBarcode,
              barcodeAssigned: true,
              barcodeAssignedAt: now,
              barcodeAssignedBy: currentUser.userId,
              // do NOT change any other field
            }
          : si,
      );

      await ItemService.updateErfService({
        erfServiceNumber: serviceRecord.erfServiceNumber,
        serviceRequiredItems: updatedItems,
      });

      // ── Step 5: Update ERF item — ONLY the barcode field ─────────────────
      await updateErfItemBarcode(item, newBarcode);

      toast.success(
        `Replacement barcode "${newBarcode}" assigned and ERF updated`,
      );
      setNewBarcodeMap((p) => ({ ...p, [item.lineItemId]: "" }));
      await fetchServiceRecord();
    } catch (err) {
      console.error("handleAssignBarcode error:", err);
      toast.error("Failed to assign replacement barcode");
    } finally {
      setLoading(false);
    }
  };

  // ── STORE: confirm all replacement barcodes assigned ──────────────────────
  const handleConfirmAllBarcodes = async () => {
    setLoading(true);
    try {
      await ItemService.updateErfService({
        erfServiceNumber: serviceRecord.erfServiceNumber,
        storeBarcodesConfirmed: true,
      });
      const confirmPayload = {
        documentNumber: activeDocNr,
        equipmentRentNr: activeDocNr,
        serviceAction: SERVICE_ACTION_CALL_STATUS.SERVICE_ASSIGNED,
      };
      if (isExtensionService) {
        await ItemService.updateExtendEquipmentRent(confirmPayload);
      } else {
        await ItemService.updateEquipmentRentForm(confirmPayload);
      }

      toast.success("All barcodes confirmed — SE can now proceed");
      await fetchServiceRecord();
    } catch (err) {
      console.error(err);
      toast.error("Failed to confirm barcodes");
    } finally {
      setLoading(false);
    }
  };

  // ── SE: mark old barcode collected from patient (no stock-in here) ─────────
  const handleReturnBarcode = async (item) => {
    const returned = (returnedBarcodeMap[item.lineItemId] || "").trim();
    if (!returned) return toast.error("Enter collected barcode");

    // Existing guard checks — keep these
    if (item.returnedToStore === true) {
      toast.error(
        "This item has already been stocked in by Store. No action needed.",
      );
      return;
    }
    if (item.returnedBarcode && item.returnedToStore === false) {
      toast.error(
        `Barcode "${item.returnedBarcode}" is already recorded as collected and is awaiting Store stock-in.`,
      );
      return;
    }
    const expectedBarcode = (item.currentBarcode || "").trim();
    if (!expectedBarcode) {
      toast.error("No current barcode on this service item. Contact admin.");
      return;
    }
    if (returned !== expectedBarcode) {
      toast.error(
        `Mismatch! Expected "${expectedBarcode}" for ${item.materialCode} but got "${returned}". Please scan the correct old item.`,
      );
      setReturnedBarcodeMap((p) => ({ ...p, [item.lineItemId]: "" }));
      return;
    }

    setLoading(true);
    try {
      const freshRes = await ItemService.generateQuery({
        queryName: "ERF_Service_Detail",
        documentNumber: serviceRecord.erfServiceNumber,
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
      });
      const freshRecord = freshRes?.data?.[0] || freshRes?.data?.data?.[0];
      if (!freshRecord) {
        throw new Error(
          "Could not load latest service record. Please refresh and try again.",
        );
      }

      // Map over FRESH DB items — never over React state
      const updatedItems = freshRecord.serviceRequiredItems.map(
        (si) =>
          si.lineItemId === item.lineItemId
            ? {
                ...si,
                returnedBarcode: returned,
                returnedToStore: false,
                collectedFromPatientAt: new Date().toISOString(),
                collectedFromPatientBy: currentUser.userId,
                collectedFromPatientByName: currentUser.name,
              }
            : si, // preserve other items exactly from DB
      );

      await ItemService.updateErfService({
        erfServiceNumber: freshRecord.erfServiceNumber,
        serviceRequiredItems: updatedItems,
      });

      toast.success(
        `"${returned}" confirmed as collected. Awaiting Store stock-in.`,
      );
      setReturnedBarcodeMap((p) => ({ ...p, [item.lineItemId]: "" }));
      await fetchServiceRecord();
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to mark collection.");
    } finally {
      setLoading(false);
    }
  };

  // ── STORE: open stock-in confirmation modal ────────────────────────────────
  const openStockInModal = (item) => setStockInModal({ item });

  // ── STORE: confirm stock-in with barcodeERFHistory push ───────────────────
  const handleStockInConfirm = async ({ condition, qualityCheck, remarks }) => {
    const { item } = stockInModal;
    setStockInModal(null);
    setLoading(true);

    try {
      const now = new Date().toISOString();

      // ── CRITICAL: Always fetch fresh record from DB ────────────────────────
      // Never use `serviceRecord` from React state here.
      // Stale closure = when item 2 is stocked in, React still holds the
      // pre-item-1-stockin state, so item 1 gets written back as false.
      const freshRes = await ItemService.generateQuery({
        queryName: "ERF_Service_Detail",
        documentNumber: serviceRecord.erfServiceNumber,
        page: 1,
        per_page: 1,
        timeZone: "Asia/Calcutta",
      });

      const freshRecord = freshRes?.data?.[0] || freshRes?.data?.data?.[0];

      if (!freshRecord) {
        throw new Error(
          "Could not load latest service record. Please refresh and try again.",
        );
      }

      // ── Validate the returned barcode has a material record ───────────────
      const matRecord = await fetchMaterialRecord(item.returnedBarcode);
      if (!matRecord) {
        throw new Error(
          `No material record found for barcode "${item.returnedBarcode}".` +
            ` Contact admin.`,
        );
      }

      // ── Update old barcode material master: mark as StockIn ───────────────
      const historyEntry = {
        status: "Stock In",
        equipmentRentNr: freshRecord.equipmentRentNr,
        equipmentItemNr: item.equipmentItemNr,
        erfTypeCode: freshRecord.erfTypeCode || "RE",
        serviceAction: SERVICE_ACTION.REPLACEMENT,
        serviceNr: freshRecord.erfServiceNumber,
        condition,
        remarks,
        qualityCheck,
        updatedAt: now,
        updatedBy: currentUser.userId,
        updatedByName: currentUser.name,
      };

      await ItemService.updateMaterialBarcodeItem({
        barcode: item.returnedBarcode,
        documentNumber: matRecord.documentNumber,
        materialStatus: "StockIn",
        barCode: "StockIn",
        barcodeERFHistory: [
          ...(matRecord.barcodeERFHistory || []),
          historyEntry,
        ],
      });

      // ── Build updated items from FRESH DB record only ─────────────────────
      // This is the only safe way — do NOT use serviceRecord.serviceRequiredItems
      const updatedItems = freshRecord.serviceRequiredItems.map(
        (si) =>
          si.lineItemId === item.lineItemId
            ? {
                ...si,
                returnedToStore: true,
                replacementComplete: true,
                stockInAt: now,
                stockInBy: currentUser.userId,
                stockInCondition: condition,
                qualityCheck,
                stockInRemarks: remarks,
              }
            : si, // all other items come from fresh DB — no stale state
      );

      // ── Update service record: ONLY serviceRequiredItems ──────────────────
      // Stock-in is a Store-only operation.
      // serviceStatus and jobDoneStatus are ONLY set by the Service Executive.
      // Never set them here regardless of how many items are stocked in.
      await ItemService.updateErfService({
        erfServiceNumber: freshRecord.erfServiceNumber,
        serviceRequiredItems: updatedItems,
      });

      // ── Progress summary for Store ────────────────────────────────────────
      const allReplacement = updatedItems.filter(
        (r) => r.serviceAction === SERVICE_ACTION.REPLACEMENT,
      );
      const doneCount = allReplacement.filter((r) => r.returnedToStore).length;

      toast.success(
        `Stock-in done for "${item.materialCode}" (${item.returnedBarcode}). ` +
          `${doneCount} of ${allReplacement.length} replacement(s) stocked in.`,
      );

      await fetchServiceRecord();
    } catch (err) {
      console.error("handleStockInConfirm error:", err);
      toast.error(err.message || "Stock-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── SE: completion update ──────────────────────────────────────────────────
  const handleSeCompletionUpdate = async () => {
    if (!serviceRecord?.erfServiceNumber) {
      return;
    }
    if (!seUpdateData.completionNotes.trim()) {
      return toast.error("Enter completion notes");
    }
    const isCompleted =
      seUpdateData.completionStatus === SERVICE_STATUS.COMPLETED;
    if (isCompleted && !completionAttachmentId) {
      return toast.error(
        "Upload completion document before marking as Completed",
      );
    }
    setLoading(true);
    try {
      await ItemService.updateErfService({
        erfServiceNumber: serviceRecord.erfServiceNumber,
        serviceStatus: seUpdateData.completionStatus,
        completedAt: isCompleted ? new Date().toISOString() : null,
        completedBy: isCompleted ? currentUser.userId : null,
        completedByName: isCompleted ? currentUser.name : null,
        completionNotes: seUpdateData.completionNotes,
        completionAttachmentId: completionAttachmentId || null,
        jobDoneStatus: isCompleted ? JOB_STATUS.DONE : JOB_STATUS.PENDING,
      });
      const completionPayload = {
        documentNumber: activeDocNr,
        equipmentRentNr: activeDocNr,
        serviceAction: isCompleted
          ? SERVICE_ACTION_CALL_STATUS.SERVICE_COMPLETED
          : SERVICE_ACTION_CALL_STATUS.SERVICE_IN_PROGRESS,
      };
      if (isExtensionService) {
        await ItemService.updateExtendEquipmentRent(completionPayload);
      } else {
        await ItemService.updateEquipmentRentForm(completionPayload);
      }
      toast.success("Service status updated");
      await fetchServiceRecord();
      navigate(Constants.CUSTOMER_V5_MR_ERF_SERVICE_LIST);
    } catch {
      toast.error("Failed to update");
    } finally {
      setLoading(false);
    }
  };

  // ── Computed: replacement workflow ────────────────────────────────────────
  const requiredItems = serviceRecord?.serviceRequiredItems || [];
  const replacementItems = requiredItems.filter(
    (r) => r.serviceAction === SERVICE_ACTION.REPLACEMENT,
  );
  const hasReplacementItems = replacementItems.length > 0;
  const allReplacementBarcodesAssigned =
    hasReplacementItems &&
    replacementItems.every((r) => r.barcodeAssigned === true);
  const storeAlreadyConfirmed = serviceRecord?.storeBarcodesConfirmed === true;
  const seCanAct = !hasReplacementItems || storeAlreadyConfirmed;

  // ── Item columns ───────────────────────────────────────────────────────────
  const itemColumns = [
    { name: "#", cell: (_, i) => i + 1, width: "50px" },
    {
      name: "Material",
      width: "220px",
      cell: (r) => (
        <div className="p-1">
          <div className="fw-bold">{r.materialCode}</div>
          <div className="text-muted small">{r.materialDescription}</div>
        </div>
      ),
    },
    {
      name: "Action",
      width: "120px",
      cell: (r) => (
        <span
          className={`badge ${
            r.serviceAction === SERVICE_ACTION.REPLACEMENT
              ? "bg-warning text-dark"
              : "bg-info text-dark"
          }`}
        >
          {r.serviceAction || "--"}
        </span>
      ),
    },
    {
      name: "Current Barcode",
      selector: (r) => r.currentBarcode || "--",
      width: "180px",
    },
    {
      name: "New Barcode",
      width: "320px",
      cell: (r) => {
        if (r.serviceAction !== SERVICE_ACTION.REPLACEMENT) return "—";
        if (r.barcodeAssigned)
          return <b className="text-success">{r.newBarcode}</b>;
        if (!isStore)
          return <span className="text-muted small">Awaiting Store</span>;
        return (
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Scan/enter new barcode"
              value={newBarcodeMap[r.lineItemId] || ""}
              onChange={(e) =>
                setNewBarcodeMap((p) => ({
                  ...p,
                  [r.lineItemId]: e.target.value,
                }))
              }
            />
            <button
              className="btnTable btn-sm"
              disabled={loading}
              onClick={() => handleAssignBarcode(r)}
            >
              Assign
            </button>
          </div>
        );
      },
    },
    {
      name: "Returned Barcode",
      width: "320px",
      cell: (r) => {
        if (r.serviceAction !== SERVICE_ACTION.REPLACEMENT) return "—";
        if (!r.barcodeAssigned)
          return <span className="text-muted small">--</span>;
        if (r.returnedToStore) {
          return (
            <div className="py-1">
              <div className="text-success fw-bold font-monospace">
                {r.returnedBarcode}
                <i className="bi bi-check-circle-fill ms-1" />
              </div>
              <small className="text-muted d-block">
                {r.stockInCondition && `Condition: ${r.stockInCondition}`}
                {r.qualityCheck && ` · QC: ${r.qualityCheck}`}
              </small>
              <small className="text-muted d-block">
                {r.stockInAt &&
                  `Stocked in: ${new Date(r.stockInAt).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`}
              </small>
            </div>
          );
        }
        if (r.returnedBarcode && !r.returnedToStore) {
          return isStore ? (
            <button
              className="btn btn-sm btn-outline-success"
              onClick={() => openStockInModal(r)}
            >
              <i className="bi bi-box-arrow-in-down me-1" /> Stock In
            </button>
          ) : (
            <span className="text-warning small">
              {r.returnedBarcode} — awaiting Store
            </span>
          );
        }
        if (!isSE) return <span className="text-muted small">Awaiting SE</span>;
        return (
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Enter collected barcode"
              value={returnedBarcodeMap[r.lineItemId] || ""}
              onChange={(e) =>
                setReturnedBarcodeMap((p) => ({
                  ...p,
                  [r.lineItemId]: e.target.value,
                }))
              }
            />
            <button
              className="btnTable btn-sm"
              disabled={loading}
              onClick={() => handleReturnBarcode(r)}
            >
              Confirm
            </button>
          </div>
        );
      },
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide quoation">
          <div className="rightSideInner PageCommonCSS">
            <Row className="mb-4">
              <Col xs={12} md={8}>
                <h2 className="mainTitle">
                  Service Detail
                  {serviceRecord?.erfServiceNumber && (
                    <span className="bg-table-status bg-table-documentNumber-status headerStatusDetailedPage ms-2">
                      {serviceRecord.erfServiceNumber}
                    </span>
                  )}
                  {/* Service status badge — always visible */}
                  {serviceRecord?.serviceStatus && (
                    <span
                      className={`ms-2 badge fs-6 ${
                        serviceRecord.serviceStatus === SERVICE_STATUS.COMPLETED
                          ? "bg-success"
                          : serviceRecord.serviceStatus ===
                              SERVICE_STATUS.CANCELLED
                            ? "bg-danger"
                            : "bg-warning text-dark"
                      }`}
                    >
                      {serviceRecord.serviceStatus}
                    </span>
                  )}
                </h2>
                <p className="subText">
                  Service record for {serviceRecord?.patientName || "—"}
                  {serviceRecord?.serviceStatus === SERVICE_STATUS.COMPLETED &&
                    serviceRecord?.completedAt && (
                      <span className="text-success ms-2 small">
                        <i className="bi bi-check-circle me-1" />
                        Completed by {serviceRecord.completedByName} on{" "}
                        {new Date(serviceRecord.completedAt).toLocaleString(
                          "en-IN",
                        )}
                      </span>
                    )}
                </p>
              </Col>
              {/* <Col xs={12} md={4} className="text-end">
    <button className="btnTable" onClick={() => navigate(-1)}>
      <i className="bi bi-arrow-left me-1" /> Back
    </button>
  </Col> */}
            </Row>
            <hr />

            <Loader isLoading={loading} className="text-center" />

            {!loading && !serviceRecord && (
              <div className="text-center p-5 text-muted">
                No service record found.
              </div>
            )}

            {serviceRecord && (
              <>
                {/* ACC 1: Patient / Service Info */}
                <div className="accordion mb-4">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col xs={1} className="text-left arrowBtn">
                          <i
                            className={`bi ${open.patient ? "bi-chevron-down" : "bi-chevron-right"}`}
                            onClick={() => toggle("patient")}
                          />
                        </Col>
                        <Col xs={1} className="text-center logoBtn">
                          <span className="iconLogo">SR</span>
                        </Col>
                        <Col xs={10}>
                          <span className="headerTitle">
                            Service / Patient Details
                          </span>
                        </Col>
                      </Row>
                    </h2>
                    {open.patient && (
                      <div className="accordion-body">
                        <Row className="mb-3">
                          {[
                            {
                              label: "ERF No",
                              value: serviceRecord.equipmentRentNr,
                            },
                            {
                              label: "Service Date",
                              value: serviceRecord.serviceDate,
                            },
                            {
                              label: "Service Time",
                              value: serviceRecord.serviceTime,
                            },
                            {
                              label: "Sale Point",
                              value: serviceRecord.salePoint,
                            },
                            {
                              label: "Patient Name",
                              value: serviceRecord.patientName,
                            },
                            { label: "Age", value: serviceRecord.age },
                            {
                              label: "Patient No",
                              value: serviceRecord.mobileNr,
                            },
                            { label: "Email", value: serviceRecord.email },
                            {
                              label: "ERF Type",
                              value: serviceRecord.erfType,
                            },
                            {
                              label: "ERF Type Code",
                              value: serviceRecord.erfTypeCode,
                            },
                            {
                              label: "Cost Centre",
                              value: serviceRecord.costCenter,
                            },
                            {
                              label: "Caregiver Name",
                              value: serviceRecord.caregiverName,
                            },
                            {
                              label: "Relationship",
                              value: serviceRecord.caregiverRelation,
                            },
                            {
                              label: "Caregiver Mobile",
                              value: serviceRecord.caregiverMobileNr,
                            },
                            {
                              label: "Service Status",
                              value: serviceRecord.serviceStatus,
                            },
                          ].map(({ label, value }) => (
                            <Col
                              key={label}
                              lg={3}
                              md={6}
                              className="commTopButtonRightLeftPadding"
                            >
                              <InputField
                                className="inputBox"
                                label={label}
                                value={value || ""}
                                disabled
                              />
                            </Col>
                          ))}
                        </Row>
                        <hr className="my-3" />
                        <Row className="mb-3">
                          {[
                            {
                              label: "Delivery Address",
                              value: serviceRecord.deliveryAddress,
                              cols: 6,
                            },
                            {
                              label: "Billing Address",
                              value: serviceRecord.billingAddress,
                              cols: 6,
                            },
                            {
                              label: "Challenges",
                              value: serviceRecord.challenges,
                              cols: 12,
                            },
                          ].map(({ label, value, cols }) => (
                            <Col
                              key={label}
                              lg={cols}
                              md={12}
                              className="commTopButtonRightLeftPadding mt-2"
                            >
                              <TextAreaField
                                className="inputBox"
                                label={label}
                                value={value || ""}
                                disabled
                                rows={2}
                              />
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </div>
                </div>

                {/* ACC 2: Service Items with barcode flow */}
                <div className="accordion mb-4">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col xs={1} className="text-left arrowBtn">
                          <i
                            className={`bi ${open.items ? "bi-chevron-down" : "bi-chevron-right"}`}
                            onClick={() => toggle("items")}
                          />
                        </Col>
                        <Col xs={1} className="text-center logoBtn">
                          <span className="iconLogo">SI</span>
                        </Col>
                        <Col xs={10}>
                          <span className="headerTitle">
                            Service Required Items
                          </span>
                        </Col>
                      </Row>
                    </h2>
                    {open.items && (
                      <div className="accordion-body p-0">
                        <DataTable
                          columns={itemColumns}
                          data={requiredItems}
                          pagination
                        />

                        {/* Store: confirm all replacement barcodes assigned */}
                        {isStore &&
                          hasReplacementItems &&
                          !storeAlreadyConfirmed && (
                            <div className="p-3 border-top">
                              <Row className="align-items-center">
                                <Col
                                  lg={8}
                                  md={8}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  {allReplacementBarcodesAssigned ? (
                                    <span className="text-success fw-semibold">
                                      <i className="bi bi-check-circle me-1" />
                                      All replacement barcodes assigned —
                                      confirm to release to SE
                                    </span>
                                  ) : (
                                    <span className="text-warning fw-semibold">
                                      <i className="bi bi-exclamation-circle me-1" />
                                      Assign barcodes for all{" "}
                                      {replacementItems.length} replacement
                                      item(s) before confirming
                                    </span>
                                  )}
                                </Col>
                                <Col
                                  lg={4}
                                  md={4}
                                  className="commTopButtonRightLeftPadding text-end"
                                >
                                  <button
                                    className="btnTable"
                                    disabled={
                                      loading || !allReplacementBarcodesAssigned
                                    }
                                    onClick={handleConfirmAllBarcodes}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    <i className="bi bi-check2-all" />
                                    Confirm All Barcodes Assigned
                                  </button>
                                </Col>
                              </Row>
                            </div>
                          )}

                        {/* Already confirmed by Store */}
                        {isStore &&
                          hasReplacementItems &&
                          storeAlreadyConfirmed && (
                            <div className="p-3 border-top">
                              <span className="text-success fw-semibold">
                                <i className="bi bi-check2-all me-1" />
                                Store confirmed — SE can proceed
                              </span>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>

                {/* ACC 3: Payment (conditional) */}
                {serviceRecord.shouldCollectMoney && (
                  <div className="accordion mb-4">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col xs={1} className="text-left arrowBtn">
                            <i
                              className={`bi ${open.money ? "bi-chevron-down" : "bi-chevron-right"}`}
                              onClick={() => toggle("money")}
                            />
                          </Col>
                          <Col xs={1} className="text-center logoBtn">
                            <span className="iconLogo">MC</span>
                          </Col>
                          <Col xs={10}>
                            <span className="headerTitle">
                              Money Collection
                            </span>
                          </Col>
                        </Row>
                      </h2>
                      {/* {open.money && (
                        <div className="accordion-body">
                          <ServicePaymentSection
                            serviceRecord={serviceRecord}
                            onRefresh={fetchServiceRecord}
                          />
                        </div>
                      )} */}
                      {open.money && (
                        <div className="accordion-body">
                          {/* Service Charges Breakdown */}
                          <Row className="mb-3 pb-3 border-bottom">
                            <Col
                              lg={3}
                              md={6}
                              className="commTopButtonRightLeftPadding"
                            >
                              <InputField
                                className="inputBox"
                                label="Net Amount (excl. GST)"
                                value={Number(
                                  serviceRecord?.serviceNetTotal || 0,
                                ).toFixed(2)}
                                disabled
                              />
                            </Col>
                            <Col
                              lg={2}
                              md={6}
                              className="commTopButtonRightLeftPadding"
                            >
                              <InputField
                                className="inputBox"
                                label="GST %"
                                value={serviceRecord?.taxPercentage ?? 18}
                                disabled
                              />
                            </Col>
                            <Col
                              lg={3}
                              md={6}
                              className="commTopButtonRightLeftPadding"
                            >
                              <InputField
                                className="inputBox"
                                label={`GST Amount (${serviceRecord?.taxPercentage ?? 18}%)`}
                                value={Number(
                                  serviceRecord?.serviceTaxTotal || 0,
                                ).toFixed(2)}
                                disabled
                              />
                            </Col>
                            <Col
                              lg={3}
                              md={6}
                              className="commTopButtonRightLeftPadding"
                            >
                              <InputField
                                className="inputBox"
                                label="Gross Total (incl. GST)"
                                value={Number(
                                  serviceRecord?.serviceGrossTotal || 0,
                                ).toFixed(2)}
                                disabled
                              />
                            </Col>
                          </Row>

                          <ServicePaymentSection
                            serviceRecord={serviceRecord}
                            onRefresh={fetchServiceRecord}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ACC 4: SE Completion Update */}
                {isSE && (
                  <div className="accordion mb-4">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col xs={1} className="text-left arrowBtn">
                            <i
                              className={`bi ${open.update ? "bi-chevron-down" : "bi-chevron-right"}`}
                              onClick={() => toggle("update")}
                            />
                          </Col>
                          <Col xs={1} className="text-center logoBtn">
                            <span className="iconLogo">SU</span>
                          </Col>
                          <Col xs={10}>
                            <span className="headerTitle">
                              SE Completion Update
                            </span>
                            {/* Show the right subtitle based on state */}
                            {serviceRecord?.serviceStatus ===
                            SERVICE_STATUS.COMPLETED ? (
                              <span className="subHeaderTitle text-success ms-2">
                                <i className="bi bi-check-circle-fill me-1" />
                                Service Completed
                              </span>
                            ) : !seCanAct ? (
                              <span className="subHeaderTitle text-warning">
                                Waiting for Store to confirm replacement
                                barcodes
                              </span>
                            ) : null}
                          </Col>
                        </Row>
                      </h2>

                      {open.update && (
                        <div className="accordion-body">
                          {/* ── Case 1: Already completed — show read-only summary ── */}
                          {serviceRecord?.serviceStatus ===
                          SERVICE_STATUS.COMPLETED ? (
                            <div className="p-3">
                              <div className="alert alert-success d-flex gap-3 align-items-start mb-0">
                                <i className="bi bi-check-circle-fill fs-3 flex-shrink-0" />
                                <div>
                                  <h6 className="fw-bold mb-2">
                                    Service Completed
                                  </h6>
                                  <div className="row g-2">
                                    <div className="col-md-4">
                                      <small className="text-muted d-block">
                                        Completed By
                                      </small>
                                      <span className="fw-semibold">
                                        {serviceRecord.completedByName || "—"}
                                      </span>
                                    </div>
                                    <div className="col-md-4">
                                      <small className="text-muted d-block">
                                        Completed At
                                      </small>
                                      <span className="fw-semibold">
                                        {serviceRecord.completedAt
                                          ? new Date(
                                              serviceRecord.completedAt,
                                            ).toLocaleString("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                              hour: "2-digit",
                                              minute: "2-digit",
                                            })
                                          : "—"}
                                      </span>
                                    </div>
                                    {serviceRecord.completionDate && (
                                      <div className="col-md-4">
                                        <small className="text-muted d-block">
                                          Completion Date
                                        </small>
                                        <span className="fw-semibold">
                                          {serviceRecord.completionDate}
                                        </span>
                                      </div>
                                    )}
                                    {serviceRecord.completionNotes && (
                                      <div className="col-12 mt-1">
                                        <small className="text-muted d-block">
                                          Notes
                                        </small>
                                        <span>
                                          {serviceRecord.completionNotes}
                                        </span>
                                      </div>
                                    )}
                                    {serviceRecord.completionAttachmentId && (
                                      <div className="col-12 mt-2 d-flex align-items-center gap-2">
                                        <small className="text-muted">
                                          Completion Document:
                                        </small>
                                        <img
                                          src="assets/img/newIcons/attached-file.png"
                                          className="attachmentIcon"
                                          alt="view document"
                                          style={{
                                            cursor: "pointer",
                                            width: 26,
                                          }}
                                          title="View completion document"
                                          onClick={() =>
                                            setFileViewModal({
                                              show: true,
                                              id: serviceRecord.completionAttachmentId,
                                            })
                                          }
                                        />
                                        <span
                                          className="text-primary small"
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            setFileViewModal({
                                              show: true,
                                              id: serviceRecord.completionAttachmentId,
                                            })
                                          }
                                        >
                                          View Document
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : !seCanAct ? (
                            /* ── Case 2: Store hasn't confirmed barcodes ── */
                            <div className="p-4 text-center text-muted">
                              <i className="bi bi-lock fs-3 d-block mb-2" />
                              Store must assign and confirm all replacement
                              barcodes before you can update the service status.
                            </div>
                          ) : (
                            /* ── Case 3: Active form ── */
                            <Row>
                              <Col
                                lg={3}
                                md={6}
                                className="commTopButtonRightLeftPadding"
                              >
                                <label className="form-label">Status</label>
                                <select
                                  className="form-control inputBox"
                                  value={seUpdateData.completionStatus}
                                  onChange={(e) =>
                                    setSeUpdateData((p) => ({
                                      ...p,
                                      completionStatus: e.target.value,
                                    }))
                                  }
                                >
                                  <option value={SERVICE_STATUS.IN_PROGRESS}>
                                    In Progress
                                  </option>
                                  <option value={SERVICE_STATUS.COMPLETED}>
                                    Completed
                                  </option>
                                  <option value={SERVICE_STATUS.CANCELLED}>
                                    Cancelled
                                  </option>
                                </select>
                              </Col>

                              <Col
                                lg={3}
                                md={6}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Completion Date"
                                  type="date"
                                  name="completionDate"
                                  value={seUpdateData.completionDate}
                                  onChange={(e) =>
                                    setSeUpdateData((p) => ({
                                      ...p,
                                      completionDate: e.target.value,
                                    }))
                                  }
                                />
                              </Col>

                              <Col
                                lg={12}
                                md={12}
                                className="commTopButtonRightLeftPadding mt-2"
                              >
                                <TextAreaField
                                  className="inputBox"
                                  label="Completion Notes"
                                  value={seUpdateData.completionNotes}
                                  onChange={(e) =>
                                    setSeUpdateData((p) => ({
                                      ...p,
                                      completionNotes: e.target.value,
                                    }))
                                  }
                                  rows={2}
                                />
                              </Col>

                              {seUpdateData.completionStatus ===
                                SERVICE_STATUS.COMPLETED && (
                                <Col
                                  lg={4}
                                  md={6}
                                  className="commTopButtonRightLeftPadding mt-2"
                                >
                                  <FileUploadComponentForHB
                                    id="completionDocument"
                                    name="completionDocument"
                                    label={
                                      <span>
                                        Completion Document{" "}
                                        <span className="text-danger">*</span>
                                      </span>
                                    }
                                    allowedTypes={[
                                      "image/png",
                                      "image/jpeg",
                                      "application/pdf",
                                    ]}
                                    multiple={false}
                                    onSuccessUpload={(data) =>
                                      setCompletionAttachmentId(
                                        data?.attachmentId || "",
                                      )
                                    }
                                    value={completionAttachmentId}
                                  />
                                  {completionAttachmentId && (
                                    <img
                                      src="assets/img/newIcons/attached-file.png"
                                      className="attachmentIcon"
                                      alt="attachment"
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        setFileViewModal({
                                          show: true,
                                          id: completionAttachmentId,
                                        })
                                      }
                                    />
                                  )}
                                  {!completionAttachmentId && (
                                    <small className="text-danger">
                                      Document is required to mark as Completed
                                    </small>
                                  )}
                                </Col>
                              )}
                              <Row className="commTopButtonRightLeftPadding mt-2 mb-2">
                                <Col
                                  xs={6}
                                  md={6}
                                  lg={2}
                                  className="mt-3 text-end"
                                >
                                  <button
                                    className="btnTable"
                                    disabled={loading}
                                    onClick={handleSeCompletionUpdate}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    <i className="bi bi-check-circle" />
                                    {loading
                                      ? "Updating..."
                                      : "Update Completion"}
                                  </button>
                                </Col>
                              </Row>
                            </Row>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ACC 5: Tally Status */}
                <ServiceTallyStatusSection
                  erfServiceNumber={serviceRecord?.erfServiceNumber}
                />
              </>
            )}
          </div>
        </Col>
      </Row>
      <FileViewerModalForHB
        show={fileViewModal.show}
        onHide={() => setFileViewModal({ show: false, id: null })}
        fileid={fileViewModal.id}
        showDelete={false}
      />

      <StockInConfirmModal
        show={!!stockInModal}
        onHide={() => setStockInModal(null)}
        item={stockInModal?.item}
        onConfirm={handleStockInConfirm}
      />
    </main>
  );
};

export default ServiceDetailPage;
